// src/components/CreateSelectionGroup.tsx

import React, { useState } from 'react';
import { Modal, Form, InputGroup, FormControl, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaInfoCircle, FaTimes } from 'react-icons/fa';
import { AddOptionToModifier } from '../../../../../../../../crud/menu_api/selection_modifiers';
import { toast } from 'react-toastify';
import MiniLoadingSpinner from '../../../../../../../../components/Spinners/MiniLoadingSpinner';
import './CreateOptionModal.scss';

interface Modifier {
    modifier_id: number;
    modifier_name: string;
    modifier_description?: string;
    local_id: number;
}

interface ModifierOption {
    option_id: number;
    modifier_id: number;
    option_name: string;
    option_description?: string;
    price_modifier: number;
}

interface ItemModifier {
    item_id: number;
    modifier_id: number;
    is_optional: number;
    is_price_setter: number;
}

interface CreateSelectionGroupProps {
  show: boolean;
  handleClose: () => void;
  mod: Modifier;
  options: ModifierOption[];
  setOptions: (items: ModifierOption[]) => void;
  onOptionCreated?: () => void; // Add optional callback
}

const CreateOptionModal: React.FC<CreateSelectionGroupProps> = ({
     show, 
     handleClose, 
     options, 
     setOptions,
     mod,
     onOptionCreated }) => {
  const [optionName, setOptionName] = useState<string>('');
  const [optionDescription, setOptionDescription] = useState<string>('');
  const [priceModifier, setPriceModifier] = useState<string>(''); // Cambiado a string vacío
  const [loading, setLoading] = useState<boolean>(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Validaciones básicas
    if (optionName.trim() === '') {
      toast.error('El nombre del grupo es obligatorio.');
      return;
    }

    // Obtener local_id del usuario (asumiendo que está almacenado en localStorage)
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const localId = user ? user.local_id : 0;

    if (localId === 0) {
      toast.error('No se pudo obtener el ID local del usuario.');
      return;
    }

    const payload = {
      option_name: optionName,
      modifier_id: mod.modifier_id,
      option_description: optionDescription.trim() !== '' ? optionDescription : undefined,
      price_modifier: priceModifier !== '' ? parseFloat(priceModifier) : 0,
    };

    AddOptionToModifier(payload)
      .then((response) => {
        toast.success('Opción creada exitosamente.');
        // Asumiendo que response.data contiene el nuevo grupo creado
        setOptions([...options, (response as any).data]);
        // Reiniciar campos
        setOptionName('');
        setOptionDescription('');
        setPriceModifier(''); // Reiniciar a vacío
        
        // Call the callback if provided
        if (onOptionCreated) {
          onOptionCreated();
        } else {
          handleClose();
        }
      })
      .catch((error) => {
        console.error('Error creando la opcion del modificador', error);
        const message = error.response?.data?.message || 'Error al crear la opción del modificador';
        toast.error(message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handlePriceModifierChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPriceModifier(e.target.value);
  }

  const handleModalClose = () => {
    if (!loading) {
      setOptionName('');
      setOptionDescription('');
      setPriceModifier(''); // Reiniciar a vacío
      handleClose();
    }
  };

  return (
    <Modal 
      show={show} 
      onHide={handleModalClose} 
      className="selection-modifiers-create-modal"
      scrollable
      fullscreen="sm-down"
    >
      <Modal.Header className="gd-header">
        <div className="form-modal-title-container">
          <h2 className="form-modal-title">Crear opción</h2>
        </div>
        <button type="button" className="modal-close-button" aria-label="Cerrar" onClick={handleModalClose}>
          <FaTimes />
        </button>
      </Modal.Header>

      {loading && <MiniLoadingSpinner />}

      {!loading && (
        <Modal.Body className="gd-body">
          <div className="gd-info-banner" role="note">
            <FaInfoCircle className="banner-icon" />
            <div className="banner-text">
              <p>Cualidad general del ítem base, por ejemplo, si la cualidad general del ítem base es <strong>tamaño de la pizza</strong>, las opciones son individual, mediano, familiar, XL.</p>
            </div>
          </div>

          <Form onSubmit={handleSubmit}>
            <div className="form-section">
              <Form.Group controlId="optionName" className="form-group">
                <Form.Label className="form-label">
                  Nombre de la opción <span className="required">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Ingrese el nombre de la opción"
                  value={optionName}
                  onChange={(e) => setOptionName(e.target.value)}
                  className="form-input"
                  required
                />
              </Form.Group>

              <Form.Group controlId="optionDescription" className="form-group">
                <Form.Label className="form-label">Descripción</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Ingrese una descripción opcional"
                  value={optionDescription}
                  onChange={(e) => setOptionDescription(e.target.value)}
                  className="form-input"
                />
              </Form.Group>

              <Form.Group controlId="priceModifier" className="form-group">
                <div className="label-with-tooltip">
                  <Form.Label className="form-label">Modificador de Precio</Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="tooltip-price-modifier">
                        Corresponde al monto que se adicionará al ítem base (producto de la carta). Por ejemplo, si el ítem de la carta es pizza napolitana y tiene un valor base de $5000, si el comensal la desea familiar serían $8000 (5000 + 3000).
                      </Tooltip>
                    }
                  >
                    <span className="tooltip-icon">
                      <FaInfoCircle />
                    </span>
                  </OverlayTrigger>
                </div>
                <InputGroup className="price-input-group">
                  <InputGroup.Text className="currency-symbol">$</InputGroup.Text>
                  <FormControl
                    type="number"
                    step="0.01"
                    placeholder=""
                    value={priceModifier}
                    onChange={handlePriceModifierChange}
                    className="form-input price-input"
                  />
                </InputGroup>
              </Form.Group>
            </div>
          </Form>
        </Modal.Body>
      )}

      <Modal.Footer className="gd-footer">
        <div className="form-modal-footer-custom">
          <button 
            type="button" 
            className="btn form-button-cancel" 
            onClick={handleModalClose} 
            disabled={loading}
          >
            Cancelar
          </button>
          <button 
            type="button" 
            className="btn form-button-create" 
            onClick={handleSubmit}
            disabled={loading || optionName.trim() === ''}
          >
            {loading ? 'Creando...' : 'Crear Opción'}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateOptionModal;
